import React from 'react';
import MenuItem from '@mui/material/MenuItem';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {Typography,Grid,Chip,useTheme,Box,Divider,Table,TableBody,TableCell,TableContainer,TableRow,Paper, TableHead} from '@mui/material';
import { tokens } from '../../../theme';
import { formattedDate, isEmpty } from '../../../Utils/Functions';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import InfoIcon from '@mui/icons-material/Info';
import { DialogSection, DialogSectionLabel, DialogSectionValue } from '../../../SystemDesign/Styles';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { translatePermissionAction, translateModuleName } from '../../../Utils/PermissionDictionary';

const SeeMoreRole = ({ row }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const role = row.original;

  // Define sections configuration
  const sections = [
    {
      id: 'basic',
      title: 'Role information',
      icon: <BadgeOutlinedIcon />,
      content: [
        { 
          label: 'Role name', 
          value: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{role.name}</Typography>
              {role.type && (
                <Chip 
                  size="small" 
                  label={{
                    EMPLOYEE: 'Employee',
                    MANAGER: 'Manager',
                    ADMIN: 'Admin',
                    SUPERADMIN: 'Super Admin',
                    INSTRUCTOR: 'Instructor',
                  }[role.type] || role.type}
                  variant="outlined"
                  color={({
                    EMPLOYEE: 'default',
                    MANAGER: 'info',
                    ADMIN: 'warning',
                    SUPERADMIN: 'error',
                    INSTRUCTOR: 'primary',
                  }[role.type]) || 'default'}
                />
              )}
            </Box>
          )
        },
        { label: 'Created at', value: formattedDate(new Date(role.createdAt)) },
        { 
          label: 'Created by', 
          value: (
            isEmpty(role.createdBy) 
              ? 'Unknown' 
              : (role.createdBy.system 
                  ? 'System' 
                  : (role.createdBy.username || role.createdBy.email || 'Unknown'))
          )
        },
        { label: 'Updated at', value: formattedDate(new Date(role.updatedAt)) }
      ]
    },
    
    {
      id: 'permissions',
      title: 'Permissions',
      icon: <LockPersonOutlinedIcon />,
      content: [
        {
          label: 'Permissions',
          value: (() => {
            const groupedByAction = role.permissions.reduce((acc, { action, model }) => {
              if (!acc[action]) acc[action] = [];
              acc[action].push(model);
              return acc;
            }, {});

            return (
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" aria-label="permissions table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          width: '25%',
                          fontWeight: 700,
                          backgroundColor: colors.grey[100],
                          borderBottom: `2px solid ${colors.grey[200]}`,
                          fontSize: '0.875rem'
                        }}
                      >
                        Action
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          backgroundColor: colors.grey[100],
                          borderBottom: `2px solid ${colors.grey[200]}`,
                          fontSize: '0.875rem'
                        }}
                      >
                        Models
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(groupedByAction).map(([action, models]) => (
                      <TableRow key={action}>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600,
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '0.875rem'
                          }}
                        >
                          {translatePermissionAction(action)}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            borderBottom: '1px solid #f0f0f0'
                          }}
                        >
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {models.map((model, idx) => (
                              <Chip
                                key={idx}
                                label={translateModuleName(model)}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.75rem',
                                  height: '22px',
                                  borderRadius: '4px',
                                  '&:hover': { backgroundColor: '#f5f5f5' }
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()
        }
      ]
    }
  ];

  // Reusable components
  const DetailItem = ({ label, value, chip, icon }) => (
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={DialogSectionLabel(colors)}>
        {icon && React.cloneElement(icon, { fontSize: 'small' })}
        {label} :
      </Typography>
      {chip ? (
        <Box sx={{ '& .MuiChip-root': { transition: 'all 0.2s', '&:hover': { opacity: 0.8 } } }}>
          {chip}
        </Box>
      ) : (
        <Typography sx={DialogSectionValue(colors)}>{value || '-'}</Typography>
      )}
    </Box>
  );

  const SectionHeader = ({ title, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2 }}>
      <Typography sx={{ ...DialogSection(colors), mr: 1 }}>
        {icon}{title}
      </Typography>
      <Divider sx={{ flexGrow: 1 }} />
    </Box>
  );

  const dialogContent = (
    <Grid container>
      {sections.map((section) => (
        <Grid item size={{ xs: 12 }} key={section.id}>
          <SectionHeader title={section.title} icon={section.icon} />
          {section.content.map((item, index) => (
            <React.Fragment key={index}>
              {item.grid ? (
                <Grid container spacing={2}>
                  {item.grid.map((gridItem, gridIndex) => (
                    <Grid item size={{ xs: 12, md: 6 }} key={gridIndex}>
                      <DetailItem {...gridItem} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <DetailItem {...item} />
              )}
            </React.Fragment>
          ))}
        </Grid>
      ))}
    </Grid>
  );

  const triggerButton = (
    <MenuItem>
      <ListItemIcon>
        <VisibilityIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>See more</ListItemText>
    </MenuItem>
  );

  return (
    <MuiDialog
      icon={<InfoIcon />}
      triggerButton={triggerButton}
      title="Role details"
      subtitle={`Role details ${role.name}`}
      content={dialogContent}
      existAction={false}
      maxWidth="md"
    />
  );
};

export default SeeMoreRole;
